import { Badge } from 'components/Common';
import { convertStatusToColor } from 'helpers/styles/statusToColor';
import { typeColor } from 'helpers/styles/typeColor';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { ORDER_STATUS } from '../../common/data/constants';
import classNames from 'classnames';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Modern styled components
const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 6px 12px;
  font-weight: 600;
  font-size: 13px;
  line-height: 24px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  background: ${({ $color, theme }) => {
    switch ($color) {
      case typeColor.primary:
        return `linear-gradient(135deg, ${theme.colors.blue50} 0%, ${theme.colors.blue100} 100%)`;
      case typeColor.danger:
        return `linear-gradient(135deg, ${theme.colors.red100} 0%, ${theme.colors.red200} 100%)`;
      case typeColor.success:
        return `linear-gradient(135deg, ${theme.colors.green100} 0%, ${theme.colors.green200} 100%)`;
      case typeColor.warning:
        return `linear-gradient(135deg, ${theme.colors.yellow100} 0%, ${theme.colors.yellow200} 100%)`;
      default:
        return `linear-gradient(135deg, ${theme.colors.gray100} 0%, ${theme.colors.gray200} 100%)`;
    }
  }};
  color: ${({ $color, theme }) => {
    switch ($color) {
      case typeColor.primary:
        return theme.colors.primary;
      case typeColor.danger:
        return theme.colors.red600;
      case typeColor.success:
        return theme.colors.green600;
      case typeColor.warning:
        return theme.colors.yellow700;
      default:
        return theme.colors.mainText;
    }
  }};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    padding: 2px;
    background: ${({ $color, theme }) => {
      switch ($color) {
        case typeColor.primary:
          return `linear-gradient(135deg, ${theme.colors.primary}40, transparent)`;
        case typeColor.danger:
          return `linear-gradient(135deg, ${theme.colors.red500}40, transparent)`;
        case typeColor.success:
          return `linear-gradient(135deg, ${theme.colors.green500}40, transparent)`;
        default:
          return 'transparent';
      }
    }};
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);

    &::before {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $color, theme }) => {
      switch ($color) {
        case typeColor.primary:
          return theme.colors.primary;
        case typeColor.danger:
          return theme.colors.red500;
        case typeColor.success:
          return theme.colors.green500;
        case typeColor.warning:
          return theme.colors.yellow500;
        default:
          return theme.colors.gray400;
      }
    }};
    box-shadow: 0 0 0 2px
      ${({ $color, theme }) => {
        switch ($color) {
          case typeColor.primary:
            return `${theme.colors.primary}30`;
          case typeColor.danger:
            return `${theme.colors.red500}30`;
          case typeColor.success:
            return `${theme.colors.green500}30`;
          case typeColor.warning:
            return `${theme.colors.yellow500}30`;
          default:
            return `${theme.colors.gray400}30`;
        }
      }};
  }

  .label-text {
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  .chevron-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    margin-left: 2px;

    svg {
      width: 14px;
      height: 14px;
      fill: none;
      stroke: currentColor;
      stroke-width: 2.5;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  &.open .chevron-icon {
    transform: rotate(180deg);
  }
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 200px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
  padding: 6px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  animation: ${fadeIn} 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray300};
    border-radius: 10px;
  }
`;

const OptionItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: ${({ theme }) => theme.colors.mainText};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${({ theme, $color }) => {
      switch ($color) {
        case typeColor.primary:
          return theme.colors.blue50;
        case typeColor.danger:
          return theme.colors.red50;
        case typeColor.success:
          return theme.colors.green50;
        case typeColor.warning:
          return theme.colors.yellow50;
        default:
          return theme.colors.gray50;
      }
    }};
    transform: translateX(4px);
  }

  &:active {
    transform: scale(0.98);
  }

  &:not(:last-child) {
    margin-bottom: 2px;
  }

  .option-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $color }) => $color};
    box-shadow: 0 0 0 3px ${({ $color }) => `${$color}20`};
    transition: all 0.2s ease;
  }

  &:hover .option-dot {
    transform: scale(1.2);
    box-shadow: 0 0 0 4px ${({ $color }) => `${$color}30`};
  }

  .option-label {
    flex: 1;
    text-align: left;
  }

  .option-shortcut {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.gray400};
    font-weight: 400;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .option-shortcut {
    opacity: 1;
  }
`;

const SelectedBadge = styled(Badge)`
  font-size: 13px;
  padding: 6px 16px;
  border-radius: 20px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: ${({ color, theme }) => {
    switch (color) {
      case typeColor.primary:
        return `linear-gradient(135deg, ${theme.colors.blue50} 0%, ${theme.colors.blue100} 100%)`;
      case typeColor.danger:
        return `linear-gradient(135deg, ${theme.colors.red50} 0%, ${theme.colors.red100} 100%)`;
      case typeColor.success:
        return `linear-gradient(135deg, ${theme.colors.green50} 0%, ${theme.colors.green100} 100%)`;
      default:
        return `linear-gradient(135deg, ${theme.colors.gray50} 0%, ${theme.colors.gray100} 100%)`;
    }
  }};
  color: ${({ color, theme }) => {
    switch (color) {
      case typeColor.primary:
        return theme.colors.primary;
      case typeColor.danger:
        return theme.colors.red600;
      case typeColor.success:
        return theme.colors.green600;
      default:
        return theme.colors.mainText;
    }
  }};
  border: 1px solid
    ${({ color, theme }) => {
      switch (color) {
        case typeColor.primary:
          return `${theme.colors.primary}30`;
        case typeColor.danger:
          return `${theme.colors.red500}30`;
        case typeColor.success:
          return `${theme.colors.green500}30`;
        default:
          return `${theme.colors.gray300}30`;
      }
    }};
`;

// SVG Icons
const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const ChangeStatus = ({
  value,
  placeholder = 'common.select',
  onChange,
  statuses = ORDER_STATUS,
  className = 'justify-content-center',
  disabled,
  skipRequestSent = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // For badge display - use ALL statuses (including request_sent)
  const applicantStatus = useMemo(() => {
    const searchValue = typeof value === 'object' ? value?.value : value;
    return statuses.find(item => item.value === searchValue) ?? null;
  }, [value, statuses]);

  // For dropdown options - filter out request_sent if skipRequestSent is true
  const dropdownStatuses = useMemo(() => {
    if (skipRequestSent) {
      return statuses.filter(status => status.value !== 'request_sent');
    }
    return statuses;
  }, [statuses, skipRequestSent]);

  const statusColor = convertStatusToColor(applicantStatus?.value);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  };

  const handleSelect = status => {
    onChange(status);
    setIsOpen(false);
  };

  if (disabled) {
    return (
      <div className={classNames('d-flex align-items-center', className)}>
        <SelectedBadge text={t(applicantStatus?.label)} color={statusColor} />
      </div>
    );
  }

  return (
    <Container ref={containerRef} className={className}>
      <TriggerButton
        $color={statusColor}
        onClick={handleToggle}
        disabled={disabled}
        className={isOpen ? 'open' : ''}
      >
        <span className="status-indicator" />
        <span className="label-text">
          {t(applicantStatus?.label ?? placeholder)}
        </span>
        <span className="chevron-icon">
          <ChevronDownIcon />
        </span>
      </TriggerButton>

      {isOpen && (
        <DropdownPanel>
          {dropdownStatuses
            .filter(status => status.value !== (value?.value || value))
            .map(status => {
              const color = convertStatusToColor(status.value);
              return (
                <OptionItem
                  key={status.value}
                  $color={color}
                  onClick={() => handleSelect(status)}
                >
                  <span className="option-dot" style={{ background: color }} />
                  <span className="option-label">{t(status.label)}</span>
                  <span className="option-shortcut">↵</span>
                </OptionItem>
              );
            })}
        </DropdownPanel>
      )}
    </Container>
  );
};
